import { CheckCircle2, Edit, User, Activity, FileText, Settings, AlertCircle, Check } from 'lucide-react';

interface FormData {
  selectedGoals: string[];
  fullName: string;
  email: string;
  phone: string;
  state: string;
  age: number | null;
  dateOfBirth: string;
  heightFeet: number | null;
  heightInches: number | null;
  weight: number | null;
  symptoms: Record<string, number>;
  activityLevel: string;
  sleepQuality: number;
  sleepHours: number | null;
  stressLevel: string;
  diet: string;
  caffeineCups: number | null;
  alcoholDrinks: number | null;
  smoking: string;
  medications: string[];
  otherMedications: string;
  allergies: string[];
  previousHormoneTherapy: string;
  hormoneTherapyDetails: string;
  medicalConditions: string[];
  medicalConditionsOther: string;
  additionalMedicalHistory: string;
  programRecommended: string;
  consultationMethod: string;
  refillFrequency: string;
  budget: string;
  additionalPreferences: string;
  consentAccurate: boolean;
  consentMonitoring: boolean;
  consentTerms: boolean;
}

interface ReviewStepProps {
  formData: FormData;
  onEditStep: (stepNumber: number) => void;
  updateFormData: (field: string, value: boolean) => void;
}

const GOAL_LABELS: Record<string, string> = {
  'vasomotor': 'Reduce hot flashes/night sweats',
  'sleep': 'Improve sleep quality',
  'libido': 'Boost libido/sexual wellness',
  'mood': 'Stabilize mood/anxiety',
  'energy': 'Enhance energy & focus',
  'metabolic': 'Metabolic/weight support',
  'vaginal_comfort': 'Vaginal comfort',
};

const ACTIVITY_LABELS: Record<string, string> = {
  'sedentary': 'Sedentary',
  'lightly_active': 'Lightly Active',
  'moderately_active': 'Moderately Active',
  'very_active': 'Very Active',
  'extremely_active': 'Extremely Active',
};

const DELIVERY_LABELS: Record<string, string> = {
  'oral': 'Oral (Pills/Capsules)',
  'topical': 'Topical (Creams/Gels)',
  'sublingual': 'Sublingual',
  'injection': 'Injections',
  'patch': 'Transdermal Patch',
  'no_preference': 'No Preference',
};

const CONSULTATION_PREFERENCES = [
  { value: 'video', label: 'Video Call (Telehealth)' },
  { value: 'phone', label: 'Phone Call' },
  { value: 'in_person', label: 'In-Person Visit' },
  { value: 'text', label: 'Secure Messaging / Text' },
];

export default function ReviewStep({ formData, onEditStep, updateFormData }: ReviewStepProps) {
  const symptomCount = Object.keys(formData.symptoms).filter(k => k !== 'additional_notes').length;

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center space-y-3">
        <p className="text-sm font-medium uppercase tracking-wider" style={{ color: '#2FFFC7' }}>
          Step 7 of 7
        </p>
        <h2 className="text-3xl font-light tracking-tight" style={{ color: '#E6E9F0' }}>
          Review Your Information
        </h2>
        <p className="text-lg" style={{ color: '#C1C9D2' }}>
          Please review your responses before submitting
        </p>
      </div>

      {/* Step 1: Goals */}
      <div 
        className="p-6 rounded-xl"
        style={{
          background: 'rgba(255, 255, 255, 0.02)',
          border: '1px solid rgba(255, 255, 255, 0.05)',
        }}
      >
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div 
              className="p-2 rounded-lg"
              style={{ background: 'rgba(47, 255, 199, 0.1)' }}
            >
              <CheckCircle2 className="h-5 w-5" style={{ color: '#2FFFC7' }} />
            </div>
            <div>
              <h3 className="text-lg font-semibold" style={{ color: '#E6E9F0' }}>
                Health Goals
              </h3>
              <p className="text-xs" style={{ color: '#9DA9B8' }}>
                Step 1 - {formData.selectedGoals.length} goals selected
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => onEditStep(1)}
            className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-all hover:opacity-80"
            style={{
              background: 'rgba(255, 255, 255, 0.05)',
              color: '#2FFFC7',
            }}
          >
            <Edit className="h-4 w-4" />
            Edit
          </button>
        </div>
        <div className="flex flex-wrap gap-2">
          {formData.selectedGoals.map((goal, index) => (
            <span
              key={goal}
              className="px-3 py-1 rounded-full text-sm"
              style={{
                background: 'rgba(47, 255, 199, 0.1)',
                color: '#2FFFC7',
              }}
            >
              {index + 1}. {GOAL_LABELS[goal] || goal}
            </span>
          ))}
        </div>
      </div>

      {/* Step 2: Personal Info */}
      <div 
        className="p-6 rounded-xl"
        style={{
          background: 'rgba(255, 255, 255, 0.02)',
          border: '1px solid rgba(255, 255, 255, 0.05)',
        }}
      >
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div 
              className="p-2 rounded-lg"
              style={{ background: 'rgba(47, 255, 199, 0.1)' }}
            >
              <User className="h-5 w-5" style={{ color: '#2FFFC7' }} />
            </div>
            <div>
              <h3 className="text-lg font-semibold" style={{ color: '#E6E9F0' }}>
                Personal Information
              </h3>
              <p className="text-xs" style={{ color: '#9DA9B8' }}>
                Step 2 - Contact & measurements
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => onEditStep(2)}
            className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-all hover:opacity-80"
            style={{
              background: 'rgba(255, 255, 255, 0.05)',
              color: '#2FFFC7',
            }}
          >
            <Edit className="h-4 w-4" />
            Edit
          </button>
        </div>
        <div className="grid md:grid-cols-2 gap-4 text-sm">
          <div>
            <p style={{ color: '#9DA9B8' }}>Name</p>
            <p style={{ color: '#E6E9F0' }}>{formData.fullName || 'Not provided'}</p>
          </div>
          <div>
            <p style={{ color: '#9DA9B8' }}>Email</p>
            <p style={{ color: '#E6E9F0' }}>{formData.email || 'Not provided'}</p>
          </div>
          <div>
            <p style={{ color: '#9DA9B8' }}>Phone</p>
            <p style={{ color: '#E6E9F0' }}>{formData.phone || 'Not provided'}</p>
          </div>
          <div>
            <p style={{ color: '#9DA9B8' }}>State</p>
            <p style={{ color: '#E6E9F0' }}>{formData.state || 'Not provided'}</p>
          </div>
          <div>
            <p style={{ color: '#9DA9B8' }}>Age</p>
            <p style={{ color: '#E6E9F0' }}>{formData.age ? `${formData.age} years` : 'Not provided'}</p>
          </div>
          <div>
            <p style={{ color: '#9DA9B8' }}>Height</p>
            <p style={{ color: '#E6E9F0' }}>
              {formData.heightFeet && formData.heightInches 
                ? `${formData.heightFeet}'${formData.heightInches}"` 
                : 'Not provided'}
            </p>
          </div>
          <div>
            <p style={{ color: '#9DA9B8' }}>Weight</p>
            <p style={{ color: '#E6E9F0' }}>{formData.weight ? `${formData.weight} lbs` : 'Not provided'}</p>
          </div>
        </div>
      </div>

      {/* Step 3: Symptoms */}
      <div 
        className="p-6 rounded-xl"
        style={{
          background: 'rgba(255, 255, 255, 0.02)',
          border: '1px solid rgba(255, 255, 255, 0.05)',
        }}
      >
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div 
              className="p-2 rounded-lg"
              style={{ background: 'rgba(47, 255, 199, 0.1)' }}
            >
              <Activity className="h-5 w-5" style={{ color: '#2FFFC7' }} />
            </div>
            <div>
              <h3 className="text-lg font-semibold" style={{ color: '#E6E9F0' }}>
                Current Symptoms
              </h3>
              <p className="text-xs" style={{ color: '#9DA9B8' }}>
                Step 3 - {symptomCount} symptom(s) reported
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => onEditStep(3)}
            className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-all hover:opacity-80"
            style={{
              background: 'rgba(255, 255, 255, 0.05)',
              color: '#2FFFC7',
            }}
          >
            <Edit className="h-4 w-4" />
            Edit
          </button>
        </div>
        <p className="text-sm" style={{ color: '#C1C9D2' }}>
          {symptomCount > 0 
            ? `You reported ${symptomCount} symptom(s) with varying severity levels.` 
            : 'No symptoms reported.'}
        </p>
      </div>

      {/* Step 4: Lifestyle */}
      <div 
        className="p-6 rounded-xl"
        style={{
          background: 'rgba(255, 255, 255, 0.02)',
          border: '1px solid rgba(255, 255, 255, 0.05)',
        }}
      >
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div 
              className="p-2 rounded-lg"
              style={{ background: 'rgba(47, 255, 199, 0.1)' }}
            >
              <Activity className="h-5 w-5" style={{ color: '#2FFFC7' }} />
            </div>
            <div>
              <h3 className="text-lg font-semibold" style={{ color: '#E6E9F0' }}>
                Lifestyle & Habits
              </h3>
              <p className="text-xs" style={{ color: '#9DA9B8' }}>
                Step 4 - Activity & wellness
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => onEditStep(4)}
            className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-all hover:opacity-80"
            style={{
              background: 'rgba(255, 255, 255, 0.05)',
              color: '#2FFFC7',
            }}
          >
            <Edit className="h-4 w-4" />
            Edit
          </button>
        </div>
        <div className="grid md:grid-cols-2 gap-4 text-sm">
          <div>
            <p style={{ color: '#9DA9B8' }}>Activity Level</p>
            <p style={{ color: '#E6E9F0' }}>
              {ACTIVITY_LABELS[formData.activityLevel] || 'Not selected'}
            </p>
          </div>
          <div>
            <p style={{ color: '#9DA9B8' }}>Sleep Quality</p>
            <p style={{ color: '#E6E9F0' }}>
              {['Very Poor', 'Poor', 'Fair', 'Good', 'Excellent'][formData.sleepQuality] || 'Not rated'}
            </p>
          </div>
          {formData.sleepHours && (
            <div>
              <p style={{ color: '#9DA9B8' }}>Sleep Hours</p>
              <p style={{ color: '#E6E9F0' }}>{formData.sleepHours} hours/night</p>
            </div>
          )}
          {formData.stressLevel && (
            <div>
              <p style={{ color: '#9DA9B8' }}>Stress Level</p>
              <p style={{ color: '#E6E9F0' }}>{formData.stressLevel}</p>
            </div>
          )}
          {formData.diet && (
            <div>
              <p style={{ color: '#9DA9B8' }}>Diet</p>
              <p style={{ color: '#E6E9F0' }}>
                {formData.diet === 'standard' ? 'Standard American Diet' :
                 formData.diet === 'mediterranean' ? 'Mediterranean' :
                 formData.diet === 'low_carb' ? 'Low Carb / Keto' :
                 formData.diet === 'plant_based' ? 'Plant-Based / Vegetarian' :
                 formData.diet === 'paleo' ? 'Paleo' :
                 formData.diet === 'other' ? 'Other' :
                 formData.diet}
              </p>
            </div>
          )}
          {formData.smoking && (
            <div>
              <p style={{ color: '#9DA9B8' }}>Smoking/Tobacco</p>
              <p style={{ color: '#E6E9F0' }}>{formData.smoking === 'yes' ? 'Yes' : 'No'}</p>
            </div>
          )}
        </div>
      </div>

      {/* Step 5: Medical History */}
      <div 
        className="p-6 rounded-xl"
        style={{
          background: 'rgba(255, 255, 255, 0.02)',
          border: '1px solid rgba(255, 255, 255, 0.05)',
        }}
      >
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div 
              className="p-2 rounded-lg"
              style={{ background: 'rgba(47, 255, 199, 0.1)' }}
            >
              <FileText className="h-5 w-5" style={{ color: '#2FFFC7' }} />
            </div>
            <div>
              <h3 className="text-lg font-semibold" style={{ color: '#E6E9F0' }}>
                Medical History
              </h3>
              <p className="text-xs" style={{ color: '#9DA9B8' }}>
                Step 5 - Health background
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => onEditStep(5)}
            className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-all hover:opacity-80"
            style={{
              background: 'rgba(255, 255, 255, 0.05)',
              color: '#2FFFC7',
            }}
          >
            <Edit className="h-4 w-4" />
            Edit
          </button>
        </div>
        <div className="space-y-3 text-sm">
          <div>
            <p style={{ color: '#9DA9B8' }}>Current Medications</p>
            {formData.medications.length > 0 ? (
              <div className="flex flex-wrap gap-2 mt-1">
                {formData.medications.map((med) => (
                  <span
                    key={med}
                    className="px-2 py-1 rounded text-xs"
                    style={{
                      background: 'rgba(47, 255, 199, 0.1)',
                      color: '#2FFFC7',
                    }}
                  >
                    {med}
                  </span>
                ))}
              </div>
            ) : (
              <p style={{ color: '#E6E9F0' }}>None reported</p>
            )}
            {formData.otherMedications && (
              <p className="mt-1 text-sm" style={{ color: '#E6E9F0' }}>
                Other: {formData.otherMedications}
              </p>
            )}
          </div>
          <div>
            <p style={{ color: '#9DA9B8' }}>Allergies</p>
            <p style={{ color: '#E6E9F0' }}>
              {formData.allergies && formData.allergies.length > 0 ? formData.allergies.join(', ') : 'No known allergies'}
            </p>
          </div>
          {formData.previousHormoneTherapy && (
            <div>
              <p style={{ color: '#9DA9B8' }}>Previous Hormone Therapy</p>
              <p style={{ color: '#E6E9F0' }}>
                {formData.previousHormoneTherapy === 'never' ? 'No, never tried' :
                 formData.previousHormoneTherapy === 'previous' ? 'Yes, previously' :
                 formData.previousHormoneTherapy === 'current' ? 'Currently using' :
                 formData.previousHormoneTherapy}
                {formData.hormoneTherapyDetails && ` - ${formData.hormoneTherapyDetails}`}
              </p>
            </div>
          )}
          {formData.medicalConditions.length > 0 && (
            <div>
              <p style={{ color: '#9DA9B8' }}>Medical Conditions</p>
              <div className="flex flex-wrap gap-2 mt-1">
                {formData.medicalConditions.map((condition) => (
                  <span
                    key={condition}
                    className="px-2 py-1 rounded text-xs"
                    style={{
                      background: 'rgba(47, 255, 199, 0.1)',
                      color: '#2FFFC7',
                    }}
                  >
                    {condition}
                  </span>
                ))}
              </div>
            </div>
          )}
          {formData.additionalMedicalHistory && (
            <div>
              <p style={{ color: '#9DA9B8' }}>Additional Medical History</p>
              <p style={{ color: '#E6E9F0' }}>{formData.additionalMedicalHistory}</p>
            </div>
          )}
        </div>
      </div>

      {/* Step 6: Preferences */}
      <div 
        className="p-6 rounded-xl"
        style={{
          background: 'rgba(255, 255, 255, 0.02)',
          border: '1px solid rgba(255, 255, 255, 0.05)',
        }}
      >
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div 
              className="p-2 rounded-lg"
              style={{ background: 'rgba(47, 255, 199, 0.1)' }}
            >
              <Settings className="h-5 w-5" style={{ color: '#2FFFC7' }} />
            </div>
            <div>
              <h3 className="text-lg font-semibold" style={{ color: '#E6E9F0' }}>
                Treatment Preferences
              </h3>
              <p className="text-xs" style={{ color: '#9DA9B8' }}>
                Step 6 - Your preferences
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => onEditStep(6)}
            className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-all hover:opacity-80"
            style={{
              background: 'rgba(255, 255, 255, 0.05)',
              color: '#2FFFC7',
            }}
          >
            <Edit className="h-4 w-4" />
            Edit
          </button>
        </div>
        <div className="space-y-3 text-sm">
          <div>
            <p style={{ color: '#9DA9B8' }}>Preferred Delivery Method</p>
            <p style={{ color: '#E6E9F0' }}>
              {DELIVERY_LABELS[formData.programRecommended] || 'Not selected'}
            </p>
          </div>
          {formData.consultationMethod && (
            <div>
              <p style={{ color: '#9DA9B8' }}>Consultation Method</p>
              <p style={{ color: '#E6E9F0' }}>
                {CONSULTATION_PREFERENCES.find(p => p.value === formData.consultationMethod)?.label || formData.consultationMethod}
              </p>
            </div>
          )}
          {formData.refillFrequency && (
            <div>
              <p style={{ color: '#9DA9B8' }}>Refill Frequency</p>
              <p style={{ color: '#E6E9F0' }}>
                {formData.refillFrequency === 'monthly' ? 'Monthly (30 days)' :
                 formData.refillFrequency === 'quarterly' ? 'Quarterly (90 days)' :
                 formData.refillFrequency === 'flexible' ? 'Flexible / As needed' :
                 formData.refillFrequency}
              </p>
            </div>
          )}
          {formData.budget && (
            <div>
              <p style={{ color: '#9DA9B8' }}>Budget</p>
              <p style={{ color: '#E6E9F0' }}>
                {formData.budget === 'under_100' ? 'Under $100/month' :
                 formData.budget === '100_200' ? '$100-200/month' :
                 formData.budget === '200_300' ? '$200-300/month' :
                 formData.budget === '300_plus' ? '$300+/month' :
                 formData.budget === 'flexible' ? 'Flexible - will invest in quality care' :
                 formData.budget}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Terms & Consent */}
      <div 
        className="p-6 rounded-xl"
        style={{
          background: 'rgba(47, 255, 199, 0.05)',
          border: '1px solid rgba(47, 255, 199, 0.2)',
        }}
      >
        <div className="flex items-start gap-3">
          <AlertCircle className="h-5 w-5 mt-0.5 flex-shrink-0" style={{ color: '#2FFFC7' }} />
          <div className="space-y-4">
            <div>
              <h3 className="font-semibold mb-2" style={{ color: '#E6E9F0' }}>
                Terms & Consent
              </h3>
              <div className="space-y-3">
                {/* Consent: Information Accurate */}
                <button
                  type="button"
                  onClick={() => updateFormData('consentAccurate', !formData.consentAccurate)}
                  className="w-full p-4 rounded-lg text-left flex items-start gap-3 transition-all"
                  style={{
                    background: formData.consentAccurate ? 'rgba(47, 255, 199, 0.1)' : 'rgba(255, 255, 255, 0.02)',
                    border: formData.consentAccurate ? '1px solid rgba(47, 255, 199, 0.5)' : '1px solid rgba(255, 255, 255, 0.2)',
                  }}
                >
                  <div 
                    className="w-5 h-5 rounded flex items-center justify-center flex-shrink-0 mt-0.5"
                    style={{ 
                      background: formData.consentAccurate ? '#2FFFC7' : 'rgba(255, 255, 255, 0.1)',
                      border: formData.consentAccurate ? 'none' : '1px solid rgba(255, 255, 255, 0.3)'
                    }}
                  >
                    {formData.consentAccurate && <Check className="w-3 h-3" style={{ color: '#000' }} />}
                  </div>
                  <span className="text-sm" style={{ color: '#C1C9D2' }}>
                    I confirm that the information provided is accurate to the best of my knowledge and I understand this will be reviewed by a licensed healthcare provider.
                  </span>
                </button>

                {/* Consent: Monitoring */}
                <button
                  type="button"
                  onClick={() => updateFormData('consentMonitoring', !formData.consentMonitoring)}
                  className="w-full p-4 rounded-lg text-left flex items-start gap-3 transition-all"
                  style={{
                    background: formData.consentMonitoring ? 'rgba(47, 255, 199, 0.1)' : 'rgba(255, 255, 255, 0.02)',
                    border: formData.consentMonitoring ? '1px solid rgba(47, 255, 199, 0.5)' : '1px solid rgba(255, 255, 255, 0.2)',
                  }}
                >
                  <div 
                    className="w-5 h-5 rounded flex items-center justify-center flex-shrink-0 mt-0.5"
                    style={{ 
                      background: formData.consentMonitoring ? '#2FFFC7' : 'rgba(255, 255, 255, 0.1)',
                      border: formData.consentMonitoring ? 'none' : '1px solid rgba(255, 255, 255, 0.3)'
                    }}
                  >
                    {formData.consentMonitoring && <Check className="w-3 h-3" style={{ color: '#000' }} />}
                  </div>
                  <span className="text-sm" style={{ color: '#C1C9D2' }}>
                    I understand that hormone therapy requires ongoing monitoring and I agree to follow up with my provider as recommended.
                  </span>
                </button>

                {/* Consent: Terms */}
                <button
                  type="button"
                  onClick={() => updateFormData('consentTerms', !formData.consentTerms)}
                  className="w-full p-4 rounded-lg text-left flex items-start gap-3 transition-all"
                  style={{
                    background: formData.consentTerms ? 'rgba(47, 255, 199, 0.1)' : 'rgba(255, 255, 255, 0.02)',
                    border: formData.consentTerms ? '1px solid rgba(47, 255, 199, 0.5)' : '1px solid rgba(255, 255, 255, 0.2)',
                  }}
                >
                  <div 
                    className="w-5 h-5 rounded flex items-center justify-center flex-shrink-0 mt-0.5"
                    style={{ 
                      background: formData.consentTerms ? '#2FFFC7' : 'rgba(255, 255, 255, 0.1)',
                      border: formData.consentTerms ? 'none' : '1px solid rgba(255, 255, 255, 0.3)'
                    }}
                  >
                    {formData.consentTerms && <Check className="w-3 h-3" style={{ color: '#000' }} />}
                  </div>
                  <span className="text-sm" style={{ color: '#C1C9D2' }}>
                    I have read and agree to the <a href="https://www.nimbus-os.com/terms" target="_blank" rel="noopener noreferrer" className="underline" style={{ color: '#2FFFC7' }} onClick={(e) => e.stopPropagation()}>Terms of Service</a> and <a href="https://www.nimbus-os.com/privacy" target="_blank" rel="noopener noreferrer" className="underline" style={{ color: '#2FFFC7' }} onClick={(e) => e.stopPropagation()}>Privacy Policy</a>.
                  </span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Next Steps Info */}
      <div 
        className="p-6 rounded-xl"
        style={{
          background: 'rgba(47, 255, 199, 0.1)',
          border: '1px solid rgba(47, 255, 199, 0.3)',
        }}
      >
        <h3 className="font-semibold mb-3" style={{ color: '#2FFFC7' }}>
          What happens next?
        </h3>
        <ol className="space-y-2 text-sm" style={{ color: '#E6E9F0' }}>
          <li className="flex gap-2">
            <span style={{ color: '#2FFFC7' }}>1.</span>
            <span>Your assessment will be reviewed by a licensed healthcare provider (typically within 24-48 hours)</span>
          </li>
          <li className="flex gap-2">
            <span style={{ color: '#2FFFC7' }}>2.</span>
            <span>Your provider will reach out to schedule a consultation</span>
          </li>
          <li className="flex gap-2">
            <span style={{ color: '#2FFFC7' }}>3.</span>
            <span>If appropriate, lab work may be recommended to establish baseline hormone levels</span>
          </li>
          <li className="flex gap-2">
            <span style={{ color: '#2FFFC7' }}>4.</span>
            <span>A personalized treatment plan will be created based on your goals and lab results</span>
          </li>
        </ol>
      </div>
    </div>
  );
}
